/** @jsx jsx */
import { memo, useCallback, useState } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import Button, { IconButton } from '@atlaskit/button/new';
import SyncIcon from '@atlaskit/icon-lab/core/sync';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import UndoIcon from '@atlaskit/icon/core/undo';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { useIntl } from '@atlassian/jira-intl';
import type { SaveResetViewSettingsButtonUI$key } from '@atlassian/jira-relay/src/__generated__/SaveResetViewSettingsButtonUI.graphql';
import type { SaveResetViewSettingsButtonUIDiscardSettingsMutation } from '@atlassian/jira-relay/src/__generated__/SaveResetViewSettingsButtonUIDiscardSettingsMutation.graphql';
import type { SaveResetViewSettingsButtonUIPublishSettingsMutation } from '@atlassian/jira-relay/src/__generated__/SaveResetViewSettingsButtonUIPublishSettingsMutation.graphql';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useFlagsService } from '@atlassian/jira-flags';
import messages from './messages.tsx';

const tooltipProps = {
	position: 'top',
	hideTooltipOnClick: true,
} as const;

type Props = {
	viewFragment: SaveResetViewSettingsButtonUI$key;
};

export const SaveResetViewSettingsButtonUI = memo(({ viewFragment }: Props) => {
	const { formatMessage } = useIntl();
	const [isModalOpen, setModalOpen] = useState(false);
	const openModal = useCallback(() => setModalOpen(true), []);
	const closeModal = useCallback(() => setModalOpen(false), []);
	const { showFlag } = useFlagsService();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const viewMetadata = useFragment(
		graphql`
			fragment SaveResetViewSettingsButtonUI on JiraIssueSearchViewMetadata
			@argumentDefinitions(staticViewInput: { type: "JiraIssueSearchStaticViewInput" }) {
				id
				... on JiraSpreadsheetView {
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
					) {
						canPublishViewConfig
					}
				}
			}
		`,
		viewFragment,
	);

	const canPublishViewConfig = viewMetadata.viewSettings?.canPublishViewConfig;

	const [publish, publishing] = useMutation<SaveResetViewSettingsButtonUIPublishSettingsMutation>(
		graphql`
			mutation SaveResetViewSettingsButtonUIPublishSettingsMutation(
				$input: JiraPublishIssueSearchConfigInput!
			) {
				jira_publishIssueSearchConfig(input: $input) {
					success
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
				}
			}
		`,
	);

	const [discard, discarding] = useMutation<SaveResetViewSettingsButtonUIDiscardSettingsMutation>(
		graphql`
			mutation SaveResetViewSettingsButtonUIDiscardSettingsMutation(
				$input: JiraDiscardUserIssueSearchConfigInput!
			) {
				jira_discardUserIssueSearchConfig(input: $input) {
					success
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
				}
			}
		`,
	);

	const onClickSave = useCallback(() => {
		closeModal();

		const showFailureFlag = () => {
			showFlag({
				messageId:
					'issue-navigator-save-reset-view-settings-button.ui.show-flag.error.save-settings-failed',
				messageType: 'transactional',
				title: messages.saveSettingsFailedTitle,
				description: messages.saveSettingsFailedMessage,
				type: 'error',
			});
		};

		publish({
			variables: {
				input: {
					viewId: viewMetadata.id,
				},
			},
			onError() {
				showFailureFlag();
			},
			onCompleted(response) {
				if (response.jira_publishIssueSearchConfig?.success !== true) {
					showFailureFlag();
					return;
				}

				showFlag({
					messageId:
						'issue-navigator-save-reset-view-settings-button.ui.show-flag.success.header.save-reset-view-settings.show-flag.success.save-settings-success',
					messageType: 'transactional',
					title: messages.saveSettingsSuccessTitle,
					description: messages.saveSettingsSuccessMessage,
					type: 'success',
				});

				fireTrackAnalytics(createAnalyticsEvent({}), 'viewSettings saved');
			},
		});

		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'saveSettings');
	}, [closeModal, createAnalyticsEvent, publish, showFlag, viewMetadata.id]);

	const onClickReset = useCallback(() => {
		const showFailureFlag = () => {
			showFlag({
				messageId:
					'issue-navigator-save-reset-view-settings-button.ui.show-flag.error.header.save-reset-view-settings.show-flag.error.reset-settings-failed',
				messageType: 'transactional',
				title: messages.resetSettingsFailedTitle,
				description: messages.resetSettingsFailedMessage,
				type: 'error',
			});
		};

		discard({
			variables: {
				input: {
					viewId: viewMetadata.id,
				},
			},
			onError() {
				showFailureFlag();
			},
			onCompleted(response) {
				if (response.jira_discardUserIssueSearchConfig?.success !== true) {
					showFailureFlag();
					return;
				}

				fireTrackAnalytics(createAnalyticsEvent({}), 'viewSettings reset');
			},
		});

		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'resetSettings', {
			isProjectAdmin: canPublishViewConfig,
		});
	}, [canPublishViewConfig, createAnalyticsEvent, discard, showFlag, viewMetadata.id]);

	if (!canPublishViewConfig) {
		return (
			<div css={styles.root}>
				<IconButton
					onClick={onClickReset}
					isLoading={discarding}
					icon={UndoIcon}
					isTooltipDisabled={false}
					label={formatMessage(messages.resetViewSettingsButton)}
					tooltip={tooltipProps}
				/>
			</div>
		);
	}

	return (
		<div css={styles.root}>
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, ...props }) => (
					<IconButton
						{...props}
						isLoading={discarding || publishing}
						icon={SyncIcon}
						label={formatMessage(messages.saveOrResetViewSettingsButton)}
						tooltip={tooltipProps}
						ref={triggerRef}
					/>
				)}
				shouldRenderToParent={false}
			>
				<DropdownItemGroup>
					<DropdownItem onClick={openModal}>
						{formatMessage(messages.saveViewSettingsMenuItem)}
					</DropdownItem>
					<DropdownItem onClick={onClickReset}>
						{formatMessage(messages.resetViewSettingsMenuItem)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>

			<ModalTransition>
				{isModalOpen && (
					<Modal onClose={closeModal} width="small">
						<ModalHeader hasCloseButton>
							<ModalTitle>{formatMessage(messages.saveConfirmationModalTitle)}</ModalTitle>
						</ModalHeader>
						<ModalBody>{formatMessage(messages.saveConfirmationModalBody)}</ModalBody>
						<ModalFooter>
							<Button appearance="subtle" onClick={closeModal}>
								{formatMessage(messages.cancelButton)}
							</Button>
							<Button appearance="primary" onClick={onClickSave}>
								{formatMessage(messages.saveButton)}
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		</div>
	);
});

const styles = cssMap({
	root: {
		display: 'flex',
		// this allows to have the gap between action buttons being animated properly upon reveal/hide
		paddingLeft: token('space.100'),
		width: '32px',
	},
});
